import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Image, Modal, Row, Table } from "react-bootstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import {
  CustomDropdownContainer,
  FormFieldLabel,
  PageHeader,
  StatusBadge,
  TableSearchField,
} from "CommonComponent";
import { hideCommonLoader, showCommonLoader } from "actions/commonActions";
import { useObjectViewedId } from "hooks/useObjectViewedId";
import {
  fetchEmailReminder,
  fetchEmailReminderOptions,
  fetchEmailReminders,
  getErrorMessage,
  updateEmailReminder,
} from "../emailSettingsApi";
import "../EmailSettings.css";

const notify = (message, type = "general_error") => ({
  type: "SHOW_SNACKBAR",
  snackbar_notification: message,
  snackbar_notification_type: type,
});

const selectMenuProps = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "fixed",
  styles: {
    menuPortal: (base) => ({ ...base, zIndex: 10050 }),
  },
};

const toForm = (row) => ({
  name: row?.name || "",
  event_code: row?.event_code || "",
  email_template_event_code: row?.email_template_event_code || "",
  reference_deadline_key: row?.reference_deadline_key || "approval_deadline",
  timing: row?.timing || "before_deadline",
  offset_days: row?.offset_days ?? 1,
  recipient_role: row?.recipient_role || "manager",
  applicable_progress_statuses: row?.applicable_progress_statuses || [],
  is_enabled: row?.is_enabled !== false && row?.is_active !== false,
  is_system: !!row?.is_system,
});

const EmailReminders = () => {
  const dispatch = useDispatch();
  const objectViewedId = useObjectViewedId();
  const [rows, setRows] = useState([]);
  const [options, setOptions] = useState(null);
  const [filter, setFilter] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(toForm(null));

  const load = useCallback(async () => {
    dispatch(showCommonLoader());
    try {
      const [list, opts] = await Promise.all([
        fetchEmailReminders(objectViewedId),
        fetchEmailReminderOptions(objectViewedId),
      ]);
      setRows(list);
      setOptions(opts);
    } catch (error) {
      dispatch(notify(getErrorMessage(error)));
    } finally {
      dispatch(hideCommonLoader());
    }
  }, [dispatch, objectViewedId]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      [
        row.name,
        row.event_code,
        row.email_template_name,
        row.reference_deadline_label,
        row.timing_label,
        row.recipient_label,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [rows, filter]);

  const openEdit = async (row) => {
    setEditingId(row.id);
    setForm(toForm(row));
    try {
      const detail = await fetchEmailReminder(row.id, objectViewedId);
      if (detail?.id) {
        setForm(toForm(detail));
      }
    } catch (error) {
      // List row already has editable fields.
    }
  };

  const closeEdit = () => {
    setEditingId(null);
    setForm(toForm(null));
  };

  const save = async () => {
    if (!editingId) return;

    if (!form.name.trim()) {
      dispatch(notify("Reminder name is required."));
      return;
    }
    if (!form.email_template_event_code) {
      dispatch(notify("Email template is required."));
      return;
    }
    if (!form.reference_deadline_key) {
      dispatch(notify("Reference deadline is required."));
      return;
    }
    if (!form.timing) {
      dispatch(notify("Timing is required."));
      return;
    }
    if (
      (form.timing === "before_deadline" || form.timing === "after_deadline") &&
      (form.offset_days === "" || form.offset_days === null || Number(form.offset_days) < 0)
    ) {
      dispatch(notify("Offset days must be 0 or greater."));
      return;
    }
    if (!form.recipient_role) {
      dispatch(notify("Recipient is required."));
      return;
    }

    dispatch(showCommonLoader());
    try {
      await updateEmailReminder(
        editingId,
        {
          name: form.name,
          email_template_event_code: form.email_template_event_code,
          reference_deadline_key: form.reference_deadline_key,
          timing: form.timing,
          offset_days:
            form.timing === "on_deadline" ? 0 : Number(form.offset_days) || 0,
          recipient_role: form.recipient_role,
          applicable_progress_statuses: form.applicable_progress_statuses,
          is_enabled: !!form.is_enabled,
          is_active: !!form.is_enabled,
        },
        objectViewedId
      );
      dispatch(notify("Email reminder saved.", "success"));
      closeEdit();
      await load();
    } catch (error) {
      dispatch(notify(getErrorMessage(error)));
    } finally {
      dispatch(hideCommonLoader());
    }
  };

  const timingOptions = (options?.timings || []).map((o) => ({
    value: o.value,
    label: o.label,
  }));
  const deadlineOptions = (options?.reference_deadlines || []).map((o) => ({
    value: o.value,
    label: o.label,
  }));
  const recipientOptions = (options?.recipient_roles || []).map((o) => ({
    value: o.value,
    label: o.label,
  }));
  const templateOptions = (options?.email_templates || []).map((o) => ({
    value: o.event_code,
    label: `${o.name} (${o.event_code})`,
  }));
  const statusOptions = (options?.progress_statuses || []).map((o) => ({
    value: o.value,
    label: o.label,
  }));

  return (
    <div className="email-settings-page">
      <PageHeader title="Email Reminders" />

      <div className="main-content-div page-card">
        <p className="email-settings-hint mb-3">
          Configure automatic email reminders triggered by application events
          and deadlines. Reminder rules control when emails are sent; Email
          Templates control what they say.
        </p>
        <TableSearchField
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="mb-3"
        />
        <Table responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Event Code</th>
              <th>Email Template</th>
              <th>Reference</th>
              <th>Timing</th>
              <th>Recipient</th>
              <th>Enabled</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={8}>No email reminders found.</td>
              </tr>
            ) : (
              filtered.map((row) => (
                <tr key={row.id}>
                  <td>{row.name || "—"}</td>
                  <td className="email-settings-mono">{row.event_code || "—"}</td>
                  <td>{row.email_template_name || row.email_template_event_code || "—"}</td>
                  <td>{row.reference_deadline_label || "—"}</td>
                  <td>{row.timing_label || "—"}</td>
                  <td>{row.recipient_label || "—"}</td>
                  <td>
                    <StatusBadge
                      label={row.is_enabled ? "Enabled" : "Disabled"}
                      variant={row.is_enabled ? "active" : "inactive"}
                    />
                  </td>
                  <td className="text-end">
                    <Button
                      variant="link"
                      className="p-0"
                      onClick={() => openEdit(row)}
                      title="Edit"
                    >
                      <Image
                        src="/assets/images/edit-icon.svg"
                        alt="Edit"
                        width={18}
                        height={18}
                      />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <Modal show={!!editingId} onHide={closeEdit} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Email Reminder</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={12}>
              <FormFieldLabel label="Reminder Name" required />
              <Form.Control
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </Col>
            <Col md={6}>
              <FormFieldLabel label="Event Code" />
              <Form.Control value={form.event_code} readOnly disabled />
            </Col>
            <Col md={6}>
              <FormFieldLabel label="Email Template" required />
              <Select
                {...selectMenuProps}
                components={{ DropdownIndicator: CustomDropdownContainer }}
                options={templateOptions}
                value={
                  templateOptions.find(
                    (o) => o.value === form.email_template_event_code
                  ) || null
                }
                onChange={(opt) =>
                  setForm((f) => ({
                    ...f,
                    email_template_event_code: opt?.value || "",
                  }))
                }
                placeholder="Select template"
              />
            </Col>
            <Col md={6}>
              <FormFieldLabel label="Reference Deadline" required />
              <Select
                {...selectMenuProps}
                components={{ DropdownIndicator: CustomDropdownContainer }}
                options={deadlineOptions}
                value={
                  deadlineOptions.find(
                    (o) => o.value === form.reference_deadline_key
                  ) || null
                }
                onChange={(opt) =>
                  setForm((f) => ({
                    ...f,
                    reference_deadline_key: opt?.value || "",
                  }))
                }
              />
            </Col>
            <Col md={6}>
              <FormFieldLabel label="Recipient" required />
              <Select
                {...selectMenuProps}
                components={{ DropdownIndicator: CustomDropdownContainer }}
                options={recipientOptions}
                value={
                  recipientOptions.find((o) => o.value === form.recipient_role) ||
                  null
                }
                onChange={(opt) =>
                  setForm((f) => ({
                    ...f,
                    recipient_role: opt?.value || "",
                  }))
                }
              />
            </Col>
            <Col md={6}>
              <FormFieldLabel label="Timing" required />
              <Select
                {...selectMenuProps}
                components={{ DropdownIndicator: CustomDropdownContainer }}
                options={timingOptions}
                value={
                  timingOptions.find((o) => o.value === form.timing) || null
                }
                onChange={(opt) =>
                  setForm((f) => ({
                    ...f,
                    timing: opt?.value || "before_deadline",
                    offset_days:
                      opt?.value === "on_deadline" ? 0 : f.offset_days || 1,
                  }))
                }
              />
            </Col>
            {form.timing !== "on_deadline" && (
              <Col md={6}>
                <FormFieldLabel label="Days Offset" required />
                <Form.Control
                  type="number"
                  min={0}
                  max={365}
                  value={form.offset_days}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      offset_days: Number(e.target.value),
                    }))
                  }
                />
                <Form.Text muted>
                  Number of days {form.timing === "after_deadline" ? "after" : "before"}{" "}
                  the reference deadline. Use 0 for the deadline day itself.
                </Form.Text>
              </Col>
            )}
            <Col md={12}>
              <FormFieldLabel label="Applicable Statuses" />
              <Select
                {...selectMenuProps}
                isMulti
                components={{ DropdownIndicator: CustomDropdownContainer }}
                options={statusOptions}
                value={statusOptions.filter((o) =>
                  (form.applicable_progress_statuses || []).includes(o.value)
                )}
                onChange={(opts) =>
                  setForm((f) => ({
                    ...f,
                    applicable_progress_statuses: (opts || []).map((o) => o.value),
                  }))
                }
              />
            </Col>
            <Col md={12}>
              <Form.Check
                type="switch"
                id="reminder-enabled"
                label="Enabled"
                checked={!!form.is_enabled}
                onChange={(e) =>
                  setForm((f) => ({ ...f, is_enabled: e.target.checked }))
                }
              />
              <Form.Text muted>
                Disabled reminders are not scheduled. Delivery content still comes
                from the linked Email Template (which must also be enabled).
              </Form.Text>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeEdit}>
            Cancel
          </Button>
          <Button variant="primary" onClick={save}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EmailReminders;
